import { useForm } from 'react-hook-form'
import { App } from 'antd'
import { TYPES } from '@/constants/quiz'
import { IAnswer } from '@/models'
import { lightItalicTextInsideBrackets } from '@/helpers'
import { Button, Text } from '@/components/ui'
import {
  EmpManyOfManyQuestion,
  EmpOneOfManyQuestion,
  EmpScaleQuestion,
  EmpTableQuestion,
  MadCompQuestion,
  MadRangeQuestion,
  ManyOfManyQuestion,
  OneOfManyQuestion,
  RangeQuestion,
  ScaleQuestion,
  TableQuestion,
  DistributedScale,
  DistributedScaleForPairSelection,
  TextoGraphic,
  RangesQuestion
} from './questionTypes'
import s from './quiz.module.scss'

interface IQuizQuestionsProps {
  questions: any[]
  onSendQuestion: (data: any, type: string, last: boolean) => void
  resetTimer: () => void
  updateMadRangeQuestion?: (data: any) => void
  madRangeQuestions?: any
  progressUuid?: string
  timerValue?: number
}

const QuizQuestions = ({
  questions,
  onSendQuestion,
  updateMadRangeQuestion,
  resetTimer,
  madRangeQuestions,
  progressUuid,
  timerValue
}: IQuizQuestionsProps) => {
  const {
    handleSubmit,
    control,
    register,
    watch,
    getValues,
    setValue,
    formState: { errors }
  } = useForm<any>()
  const { message } = App.useApp()

  const cantSavePageWithMoreThanOneQuestion = {
    emp_scale: true,
    mad_range_image: true,
    mad_range_text: true,
    mad_comp_image: true,
    mad_comp_text: true
  }

  const isDifficultPage = Boolean(questions.length === 1 && cantSavePageWithMoreThanOneQuestion[questions[0]?.type])

  const onSubmit = async (formData: any) => {
    const isValidateSuccess = (question: any, answerData: any) => {
      if (question.type === TYPES.ONE_OF_MANY) {
        if (!answerData.answer_uuid) {
          return !message.error(`Выберите ответ`)
        }

        if (answerData.answer_uuid === 'FREE_FORM') {
          if (!answerData.free_form_answer?.trim()) {
            return !message.error(`Введите свой вариант`)
          }

          if (answerData.free_form_answer?.toString()?.length > 500) {
            return !message.error(
              `Количество символов не должно превышать 500, сейчас ${answerData.free_form_answer?.length}`
            )
          }
        }
      }

      if (question.type === TYPES.MANY_OF_MANY) {
        const selectedAnswerIds: string[] = []

        Object.entries(answerData.answer_uuid).forEach((v: any) => {
          if (v[1]) {
            selectedAnswerIds.push(v[0])
          }
        })

        const selectedAnswersLength = selectedAnswerIds.length
        const freeFormRowsIndex = selectedAnswerIds
          .filter((v: string) => v.match(/FREE_FORM-d{0,}[0-9]/g))
          .map((v: string) => v.match(/\d/g))
          .flat()

        if (!selectedAnswersLength) {
          return !message.error(`Выберите ответ`)
        }

        if (selectedAnswersLength > question.max_user_answers) {
          return !message.error(
            `Максимальное число вариантов для выбора не должно превышать ${question.max_user_answers}`
          )
        }

        // eslint-disable-next-line no-restricted-syntax
        for (const index of freeFormRowsIndex) {
          // @ts-ignore
          if (!answerData.free_form_answer[index].trim()) {
            return !message.error('Заполните поле выбранного ответа')
          }

          if (answerData.free_form_answer?.toString()?.length > 500) {
            return !message.error(
              `Количество символов не должно превышать 500, сейчас ${answerData.free_form_answer?.length}`
            )
          }
        }
      }

      if (question.type === TYPES.TABLE) {
        const tableValues = Object.values(answerData)

        if (tableValues.length !== tableValues.filter((v: unknown | string) => v).length) {
          return !message.error('Заполните таблицу')
        }
      }

      if (question.type === TYPES.EMP_ONE_OF_MANY && question.users?.length && !answerData) {
        return !message.error(`Выберите оценку`)
      }

      if (question.type === TYPES.EMP_MANY_OF_MANY && question.users?.length) {
        const selectedAnswerIds: string[] = []
        answerData.forEach((v: boolean | string) => v && selectedAnswerIds.push(v as string))

        if (!selectedAnswerIds.length) {
          return !message.error(`Выберите оценку`)
        }

        if (selectedAnswerIds.length > question.max_user_answers) {
          return !message.error(
            `Максимальное число вариантов для выбора не должно превышать ${question.max_user_answers}`
          )
        }
      }

      if (question.type === TYPES.EMP_TABLE && question.users?.length) {
        const tableValues = Object.values(answerData)

        if (tableValues.length !== tableValues.filter((v: unknown | string) => v).length) {
          return !message.error('Заполните таблицу')
        }
      }

      if (question.type === TYPES.RANGE) {
        if (Object.values(answerData).length !== question.answers_objects.length) {
          return !message.error('Пронумеруйте все варианты ответа')
        }
      }

      if (question.type === TYPES.DISTRIBUTED_SCALE) {
        if (Object.values(answerData || {})?.reduce((a: number, b: any) => (a || 0) + (b || 0), 0) < question.balli) {
          return !message.error('У Вас остались нераспределенные баллы')
        }
      }

      return true
    }

    const getAnswerData = (question: any, answerData: any) => {
      if (question.type === TYPES.SCALE)
        return { answer_uuid: question.answers?.find((item: any) => item?.value === answerData?.answer_uuid).uuid }

      if (question.type === TYPES.ONE_OF_MANY) {
        if (answerData.answer_uuid === 'FREE_FORM') return { free_form_answer: answerData.free_form_answer }
        return {
          answer_uuid: question.answers.find((v: IAnswer) => v.uuid === answerData.answer_uuid).uuid,
          free_form_answer: ''
        }
      }

      if (question.type === TYPES.MANY_OF_MANY) {
        const selectedAnswerIds: string[] = []

        Object.entries(answerData.answer_uuid).forEach((v: any) => v[1] && selectedAnswerIds.push(v[0]))

        const freeFormRowsIndex = selectedAnswerIds
          .filter((v: string) => v.match(/FREE_FORM-d{0,}[0-9]/g))
          .map((v: string) => v.match(/\d/g))
          .flat()

        return {
          answers_uuid: selectedAnswerIds.filter((v: string) => !v.match(/FREE_FORM-d{0,}[0-9]/g)),
          free_form_answer: answerData?.free_form_answer
            ? answerData.free_form_answer.filter((v: string, i: number) => freeFormRowsIndex.includes(String(i)))
            : []
        }
      }

      if (question.type === TYPES.TABLE) {
        const result: {
          answer_object_uuid: string
          scale_object_uuid: unknown | string
        }[] = []

        Object.entries(answerData).forEach((v) => {
          result.push({
            answer_object_uuid: v[0],
            scale_object_uuid: v[1]
          })
        })

        return { table_select: result }
      }

      if (question.type === TYPES.EMP_ONE_OF_MANY) {
        if (!question.users?.length) return {}
        return { user_uuid: answerData, value: 1 / question.users.length }
      }

      if (question.type === TYPES.EMP_MANY_OF_MANY) {
        if (!question.users?.length) return {}

        const selectedAnswerIds: string[] = []
        answerData.forEach((v: boolean | string) => v && selectedAnswerIds.push(v as string))

        return {
          params: selectedAnswerIds.map((userUuid: string) => ({
            user_uuid: userUuid,
            value: 1 / question.users.length
          }))
        }
      }

      if (question.type === TYPES.EMP_TABLE) {
        const result: {
          scale_object_uuid: unknown | string
          user_uuid: string
          value: number
        }[] = []

        Object.entries(answerData).forEach((v) => {
          // @ts-ignore
          const scaleUuidWIthValue = v[1]?.split('split')
          result.push({
            scale_object_uuid: scaleUuidWIthValue[0],
            user_uuid: v[0],
            value: Number(scaleUuidWIthValue[1])
          })
        })

        return { params: result }
      }

      if (question.type === TYPES.RANGE) {
        return {
          range_select: answerData.map((v: IAnswer, i: number) => ({
            answer_object_uuid: v.uuid,
            position_value: question.position_values[i]
          }))
        }
      }

      if (question.type === TYPES.DISTRIBUTED_SCALE) {
        const result: {
          answer_uuid: string
          balli: unknown | number
        }[] = []

        Object.entries(answerData).forEach((v) => {
          result.push({
            answer_uuid: v[0],
            balli: v[1] || 0
          })
        })

        return { answers: result }
      }

      if (question.type === TYPES.DISTRIBUTED_SCALE_FOR_PAIR_SELECTION) {
        const result: { answer_uuid: string; balli: number }[] = []

        const noAnswerUuid = Object.entries(answerData)
          ?.map(([uuid, value]: [string, unknown | number]) => {
            if (value === 99) return uuid
            return null
          })
          .filter(Boolean)
        if (noAnswerUuid[0]) return { answers: { answer_uuid: noAnswerUuid[0], balli: 99 } }
        Object.entries(answerData)?.forEach(([uuid, value]: [string, unknown | number]) => {
          const correctValue: number = Number(value) || 0
          const splitScaleUuid = uuid?.split('split')
          if (splitScaleUuid.length < 2) return
          result.push({ answer_uuid: splitScaleUuid[0], balli: question.balli - correctValue })
          result.push({ answer_uuid: splitScaleUuid[1], balli: correctValue })
        })

        return { answers: result }
      }

      if (question.type === TYPES.RANGES) return { value: Number(answerData?.value) }

      if (!question.type && question.id > 1000000) {
        return {
          progress_uuid: progressUuid,
          question_id: question.id,
          type: 'Textpgraphic_list'
        }
      }
    }

    const validatedFields = {}

    questions.forEach((v: any) => {
      validatedFields[v.id] = isValidateSuccess(v, formData?.[v.id])
    })

    function* enumerate(iterable: any) {
      let i = 0

      // eslint-disable-next-line no-restricted-syntax
      for (const x of iterable) {
        yield [i, x]
        i++
      }
    }

    const validateSuccess = Object.values(validatedFields).indexOf(false) === -1

    if (validateSuccess) {
      let finalData: any[]

      resetTimer()

      if (isDifficultPage) {
        finalData = [
          {
            question_id: questions[0].id,
            type: questions[0].type,
            is_cant_answer: false,
            ...formData
          }
        ]
      } else {
        finalData = questions.map((v: any) => ({
          question_id: v.id,
          type: v.type,
          is_cant_answer: false,
          ...getAnswerData(v, formData?.[v.id])
        }))
      }

      // eslint-disable-next-line no-restricted-syntax
      for (const [i, answerItem] of enumerate(finalData)) {
        // eslint-disable-next-line no-await-in-loop
        await onSendQuestion(answerItem, answerItem.type, i + 1 === finalData.length)
      }
    }
  }

  const questionComponent = (question: any) => {
    switch (question?.type) {
      case TYPES.SCALE:
        return <ScaleQuestion question={question} control={control} errors={errors} setValue={setValue} />
      case TYPES.ONE_OF_MANY:
        return <OneOfManyQuestion question={question} register={register} setValue={setValue} />
      case TYPES.MANY_OF_MANY:
        return <ManyOfManyQuestion question={question} register={register} setValue={setValue} getValues={getValues} />
      case TYPES.TABLE:
        return <TableQuestion question={question} register={register} setValue={setValue} />
      case TYPES.EMP_SCALE:
        return <EmpScaleQuestion question={question} sendQuestion={onSubmit} />
      case TYPES.EMP_ONE_OF_MANY:
        return <EmpOneOfManyQuestion question={question} register={register} setValue={setValue} />
      case TYPES.EMP_MANY_OF_MANY:
        return (
          <EmpManyOfManyQuestion question={question} register={register} getValues={getValues} setValue={setValue} />
        )
      case TYPES.EMP_TABLE:
        return <EmpTableQuestion question={question} register={register} setValue={setValue} />
      case TYPES.RANGE:
        return <RangeQuestion question={question} control={control} watch={watch} />
      case TYPES.MAD_RANGE_TEXT:
      case TYPES.MAD_RANGE_IMAGE:
        return (
          <MadRangeQuestion
            question={question}
            sendQuestion={onSubmit}
            updateMadRangeQuestion={updateMadRangeQuestion}
          />
        )
      case TYPES.MAD_COMP_TEXT:
      case TYPES.MAD_COMP_IMAGE:
        return <MadCompQuestion question={question} sendQuestion={onSubmit} madRangeQuestions={madRangeQuestions} />
      case TYPES.DISTRIBUTED_SCALE:
        return <DistributedScale question={question} control={control} getValues={getValues} setValue={setValue} />
      case TYPES.DISTRIBUTED_SCALE_FOR_PAIR_SELECTION:
        return <DistributedScaleForPairSelection question={question} control={control} setValue={setValue} />
      case TYPES.RANGES:
        return <RangesQuestion question={question} register={register} errors={errors} setValue={setValue} />
      case undefined:
        return <TextoGraphic question={question} />
      default:
        return null
    }
  }

  const quizQuestionContent = () =>
    questions?.map((v: any) => (
      <div className={s['question-item']} key={v?.id}>
        {v?.title && v?.id < 1000000 && (
          <Text as='p' className={s['question-item__title']}>
            {lightItalicTextInsideBrackets(v.title)}
          </Text>
        )}
        {timerValue !== undefined && (
          <div className={s['time-limit']}>
            <Text color='red'>Время ограничено</Text>
            <div className={s.timer}>
              <Text size='xl'>{Math.floor(timerValue / 60)}</Text>
              <Text size='sm' color='gray'>
                мин.
              </Text>
              <Text size='xl'>{timerValue % 60}</Text>
              <Text size='sm' color='gray'>
                сек.
              </Text>
            </div>
          </div>
        )}
        <div className={s['question-item__content']}>{questionComponent(v)}</div>
      </div>
    ))

  // TODO quiz
  // merge getAnswerData and isValidateSuccess functions

  if (!questions) return null

  if (isDifficultPage) {
    return <div className={s['quiz-questions']}>{quizQuestionContent()}</div>
  }

  return (
    <form className={s['quiz-questions']} onSubmit={handleSubmit(onSubmit)}>
      {quizQuestionContent()}
      <Button type='submit' className={s['quiz-questions__next-button']}>
        Продолжить
      </Button>
    </form>
  )
}

export default QuizQuestions
